/**
 * World hover tooltip — the "what is that thing under my cursor?" affordance.
 *
 * Lean v1 of `docs/design/15-hover-tooltips.md`: a single cursor-anchored DOM
 * panel that names whatever the player hovers (resource node, creature,
 * building, allied/enemy hero) and shows its one key stat — HP for damageables,
 * remaining amount for resource deposits — plus a short flavor line where it
 * teaches the player something (deposits + buildings).
 *
 * Client-only: content is built from the locally-synced Colyseus state, no
 * network round-trip per hover. main.ts owns the per-frame raycast + pick (it
 * already has that infrastructure for right-click) and feeds the resolved
 * `HoverContent` here; this module owns debounce, positioning, and DOM.
 *
 * The display strings below are the v1 `tooltip-strings` source the spec calls
 * for (§"Build phase notes"). They live here rather than in the shared
 * registries because they're pure presentation — names/flavor have no gameplay
 * reader. Creature names are the exception: they're shared via
 * `../display-names` so the click/target overlay and pet frame can't diverge
 * from hover.
 */

import { creatureDisplayName } from "../display-names";
import { harvestCursorForKind } from "@tribez/shared";
import type { CursorState } from "./cursor";

// ── content model ────────────────────────────────────────────────────────────

export type HoverRelation = "own" | "ally" | "enemy" | "neutral";

export type HoverContent = {
  /** Stable id for debounce — re-hovering the SAME entity keeps the panel up. */
  key: string;
  name: string;
  /** Optional category / owner line under the name. */
  sub?: string;
  relation?: HoverRelation;
  /** Damageable entities. Rendered red below 25%. */
  hp?: { cur: number; max: number };
  /** Resource deposits — labelled by resource (Lumber/Stone/…), not unit name. */
  amount?: { label: string; cur: number; max: number };
  /** Static flavor / what-it-does line. */
  desc?: string;
  /** Which themed cursor to show while hovering this entity (undefined → default). */
  cursor?: CursorState;
};

// ── display-string tables (v1 tooltip-strings, ported from the spec) ──────────

/** Resource kinds that are gatherable deposits: header name + resource label +
 *  flavor. Kinds absent here (ground drops, saplings) get a name-only tooltip. */
const DEPOSIT_STRINGS: Record<string, { name: string; label: string; desc: string }> = {
  MapleTree:        { name: "Tree",          label: "Lumber",         desc: "Cut down to get lumber." },
  ElderTree:        { name: "Elder Tree",    label: "Infused Lumber", desc: "Cut down for rare infused lumber." },
  StoneDeposit:     { name: "Stone Deposit", label: "Stone",          desc: "Mine for stone — and a chance at rare gems." },
  GoldDeposit:      { name: "Gold Deposit",  label: "Gold",           desc: "Mine for precious gold. Tiring work." },
  BerryBush:        { name: "Berry Bush",    label: "Berries",        desc: "Ripe delicious berries, yeah!" },
  CulinaryHerbBush: { name: "Herb Bush",     label: "Herbs",          desc: "Forage culinary herbs for cooking." },
};

/** Non-deposit resource pickups → display name (name-only tooltip). */
const PICKUP_NAMES: Record<string, string> = {
  RawStagMeat: "Raw Stag Meat", RawLamb: "Raw Bison Meat", RawFox: "Raw Fox Meat",
  Leather: "Leather", Wool: "Wool", Antlers: "Antlers",
  BearPelt: "Cave Bear Pelt", RawBearMeat: "Raw Cave Bear Meat", WhiteBearFang: "Cave Bear Fang",
  RawWolfMeat: "Raw Game Meat", SkeletalFinger: "Skeletal Finger",
  Sageberry: "Sageberry", SpicyHerbs: "Spicy Herbs", Nightcrawler: "Nightcrawler",
  AncientArtifactFragment: "Ancient Artifact Fragment",
  Shadowstone: "Shadowstone", RadiantGem: "Radiant Gem", Sapling: "Sapling",
};

/** Building type → display name + flavor. */
const BUILDING_STRINGS: Record<string, { name: string; desc: string }> = {
  Tent:                   { name: "Tent",            desc: "Boosts stamina regen for nearby allies." },
  Campfire:               { name: "Campfire",        desc: "Provides heat, wards off predators, cooks food." },
  Palisade:               { name: "Palisade",        desc: "A wall that blocks enemies." },
  Forge:                  { name: "Forge",           desc: "Craft weapons and armor." },
  Workbench:              { name: "Workbench",        desc: "Craft tools and gear." },
  HuntersLodge:           { name: "Hunter's Lodge",  desc: "Tame and train pets." },
  StorageOutpost:         { name: "Storage Outpost", desc: "Deposit items here for allies." },
  Tavern:                 { name: "Tavern",          desc: "Buy services — at a price, of course." },
  Farm:                   { name: "Farm",            desc: "Cultivate food and bait." },
  AutomatedDefenseTurret: { name: "Defense Turret",  desc: "Auto-fires on nearby enemies." },
  SpikeTrap:              { name: "Spike Trap",       desc: "Damages enemies that step on it." },
  Wonder:                 { name: "Wonder",          desc: "Build to win the match." },
};

// ── content builders (pure — unit-testable) ──────────────────────────────────

export function resourceHover(id: string, kind: string, hp: number, hpMax: number): HoverContent {
  const dep = DEPOSIT_STRINGS[kind];
  if (dep) {
    return {
      key: `resource:${id}`,
      name: dep.name,
      relation: "neutral",
      amount: { label: dep.label, cur: hp, max: hpMax },
      desc: dep.desc,
      cursor: `harvest-${harvestCursorForKind(kind)}`,
    };
  }
  return {
    key: `resource:${id}`,
    name: PICKUP_NAMES[kind] ?? kind,
    relation: "neutral",
    cursor: `harvest-${harvestCursorForKind(kind)}`,
  };
}

export function creatureHover(id: string, type: string, hp: number, hpMax: number, friendly = false): HoverContent {
  // `friendly` = the player's own or an ally's pet. Such creatures aren't
  // attackable (the server rejects it), so they get the ally tint + no attack
  // cursor — they read as selectable, not as a target (Aviv 2026-06-30).
  return {
    key: `creature:${id}`,
    name: creatureDisplayName(type),
    relation: friendly ? "ally" : "neutral",
    hp: { cur: hp, max: hpMax },
    cursor: friendly ? undefined : "attack",
  };
}

export function buildingHover(
  id: string,
  type: string,
  hp: number,
  hpMax: number,
  relation: HoverRelation,
  ownerName: string,
): HoverContent {
  const b = BUILDING_STRINGS[type] ?? { name: type, desc: "" };
  const sub =
    relation === "own" ? "Yours"
    : relation === "ally" ? (ownerName ? `Ally · ${ownerName}` : "Ally")
    : "Enemy";
  return {
    key: `building:${id}`,
    name: b.name,
    sub,
    relation,
    hp: { cur: hp, max: hpMax },
    desc: b.desc || undefined,
  };
}

export function cubeHover(
  sessionId: string,
  name: string,
  isBot: boolean,
  level: number,
  hp: number,
  hpMax: number,
  relation: HoverRelation,
): HoverContent {
  const who = name || (isBot ? "Bot" : "Kobold");
  const team = relation === "ally" ? "Ally" : relation === "enemy" ? "Enemy" : "";
  const sub = [team, `Level ${level}`, isBot ? "Bot" : ""].filter(Boolean).join(" · ");
  return {
    key: `cube:${sessionId}`,
    name: who,
    sub,
    relation,
    hp: { cur: hp, max: hpMax },
    cursor: relation === "enemy" ? "attack" : undefined,
  };
}

// ── DOM panel ─────────────────────────────────────────────────────────────────

const ACTIVATE_MS = 150;   // stationary-on-entity debounce (spec §"Trigger model")
const CURSOR_OFFSET = 14;   // px gap from cursor
const EDGE_PAD = 8;         // viewport clamp padding

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&quot;");
}

export type HoverTooltip = {
  /** Per-frame. `content=null` hides; `sx/sy` are cursor clientX/clientY. */
  update(content: HoverContent | null, sx: number, sy: number): void;
  dispose(): void;
};

export function createHoverTooltip(): HoverTooltip {
  const el = document.createElement("div");
  el.id = "hover-tooltip";
  el.classList.add("hidden");
  document.body.appendChild(el);

  let currentKey: string | null = null;
  let hoverStart = 0;
  let visible = false;

  function render(c: HoverContent): void {
    const parts: string[] = [];
    const relClass = c.relation ? ` ht-name--${c.relation}` : "";
    parts.push(`<div class="ht-name${relClass}">${esc(c.name)}</div>`);
    if (c.sub) parts.push(`<div class="ht-sub">${esc(c.sub)}</div>`);

    const rows: string[] = [];
    if (c.hp) {
      const low = c.hp.max > 0 && c.hp.cur / c.hp.max < 0.25 ? " ht-val--low" : "";
      rows.push(`<div class="ht-row"><span>HP</span><span class="ht-val${low}">${Math.ceil(c.hp.cur)} / ${c.hp.max}</span></div>`);
    }
    if (c.amount) {
      rows.push(`<div class="ht-row"><span>${esc(c.amount.label)}</span><span class="ht-val">${Math.ceil(c.amount.cur)} / ${c.amount.max}</span></div>`);
    }
    if (rows.length) parts.push(`<div class="ht-stats">${rows.join("")}</div>`);
    if (c.desc) parts.push(`<div class="ht-desc">${esc(c.desc)}</div>`);
    el.innerHTML = parts.join("");
  }

  function position(sx: number, sy: number): void {
    // Render-then-measure so flips use real dimensions. Default quadrant is
    // down-right of the cursor; flip toward the cursor if it would clip.
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let x = sx + CURSOR_OFFSET;
    let y = sy + CURSOR_OFFSET;
    if (x + w > window.innerWidth - EDGE_PAD) x = sx - CURSOR_OFFSET - w;
    if (y + h > window.innerHeight - EDGE_PAD) y = sy - CURSOR_OFFSET - h;
    x = Math.max(EDGE_PAD, x);
    y = Math.max(EDGE_PAD, y);
    el.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  }

  return {
    update(content, sx, sy) {
      if (!content) {
        if (visible) { el.classList.add("hidden"); visible = false; }
        currentKey = null;
        return;
      }
      const now = performance.now();
      if (content.key !== currentKey) {
        currentKey = content.key;
        hoverStart = now;
      }
      if (now - hoverStart < ACTIVATE_MS) return; // still debouncing
      // Re-render every frame so live HP / amount track depletion.
      render(content);
      if (!visible) { el.classList.remove("hidden"); visible = true; }
      position(sx, sy);
    },
    dispose() {
      el.remove();
    },
  };
}
